import type { Texture } from "pixi.js"
import type { ComponentType } from "react"
import { useEffect, useMemo, useState } from "react"
import { ICON_RESOLUTION_MULTIPLIER } from "@/domains/visualization/constants/graph"
import type { IconColors } from "@/domains/visualization/types/Graph"
import { iconToTexture } from "@/domains/visualization/utils/iconToTexture/iconToTexture"
import { logger } from "@/shared/utils/add/logger/logger"

// Type for components that might have a render property with name
type ComponentWithRender = ComponentType<{
  className?: string
  style?: React.CSSProperties
  color?: string
  primaryColor?: string
  secondaryColor?: string
}> & {
  render?: { name?: string }
}

// Helper to get component name
function getComponentName(component: ComponentWithRender): string {
  if (component.name) return component.name
  const componentWithRender = component
  return componentWithRender.render?.name ?? "unknown"
}

interface IconTextureOptions {
  isActive?: boolean
  size?: number
  // Fragments icon props
  FragmentsIconComponent: ComponentType<{
    className?: string
    style?: React.CSSProperties
    color?: string
    primaryColor?: string
    secondaryColor?: string
  }>
  colors?: IconColors
}

// Global texture cache - share identical textures
const textureCache = new Map<string, Texture>()

// Cleanup: remove old textures when cache gets too big
const MAX_CACHE_SIZE = 20
const cleanupCache = () => {
  if (textureCache.size > MAX_CACHE_SIZE) {
    // Remove half the cache (oldest entries)
    const keysToRemove = Array.from(textureCache.keys()).slice(0, Math.floor(textureCache.size / 2))
    keysToRemove.forEach((key) => {
      const texture = textureCache.get(key)
      if (texture) {
        texture.destroy()
        textureCache.delete(key)
      }
    })
  }
}

export function useIconTexture({ isActive = true, size, FragmentsIconComponent, colors }: IconTextureOptions) {
  const [texture, setTexture] = useState<Texture | null>(null)

  // Create cache key for texture sharing
  const cacheKey = useMemo(() => {
    const componentName = getComponentName(FragmentsIconComponent)
    const sizeStr = String(size ?? 16)
    const activeStr = String(isActive)
    const colorStr = `${colors?.color ?? "default"}-${colors?.primaryColor ?? "default"}-${colors?.secondaryColor ?? "default"}`
    return `${componentName}-${sizeStr}-${activeStr}-${colorStr}`
  }, [FragmentsIconComponent, size, isActive, colors])

  useEffect(() => {
    let isCancelled = false

    // Check cache first
    const cachedTexture = textureCache.get(cacheKey)
    if (cachedTexture) {
      setTexture(cachedTexture)
      return
    }

    // Create new texture if not cached
    const generateTexture = async () => {
      try {
        const newTexture = await iconToTexture(FragmentsIconComponent, {
          size,
          isActive,
          colors
        })
        if (!isCancelled) {
          // Cache and use the texture
          textureCache.set(cacheKey, newTexture)
          cleanupCache()
          setTexture(newTexture)
        }
      } catch (error) {
        logger.error("IconTexture: Failed to generate texture", { error })
      }
    }

    void generateTexture()

    return () => {
      isCancelled = true
    }
  }, [cacheKey, FragmentsIconComponent, size, isActive, colors])

  return {
    texture,
    scale: 1 / ICON_RESOLUTION_MULTIPLIER
  }
}
